import React, { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, Plus, Menu as MenuIcon, X } from "lucide-react";
import { translations, menuData, galleryImages, allergenList } from "../data/content";
import { AllergenIcon } from "./AllergenIcons";
import ConsentBanner from "./ConsentBanner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_chalk-pizza/artifacts/ug4iix5v_ChatGPT%20Image%2019%20giu%202026%2C%2012_15_11.png";

const TGTG_LOGO_URL = "/tgtg-logo.svg";
const TGTG_URL = "https://share.toogoodtogo.com/item/241096850945415744/";
const WHATSAPP_NUMBER = "393331417551";

const NAV_ITEMS = [
  { id: "home", key: "home" },
  { id: "menu", key: "menu" },
  { id: "gallery", key: "gallery" },
  { id: "address", key: "address" },
  { id: "hours", key: "hours" },
  { id: "contact", key: "contact" },
  { id: "allergens", key: "allergens" },
];

const PizzaSite = () => {
  const [lang, setLang] = useState("it");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAcc, setOpenAcc] = useState(null);
  const t = translations[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Open cookie preferences automatically if URL hash requests it
  useEffect(() => {
    const check = () => {
      if (window.location.hash === "#open-cookie-preferences") {
        window.dispatchEvent(new Event("ptr-open-preferences"));
        // clean hash so it doesn't retrigger on nav
        history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  return (
    <div className="chalkboard min-h-screen text-[#f4f4f0] selection:bg-[#800020]">
      {/* NAV */}
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#121212]/75 border-b border-[#800020]/25"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between">
          <button
            data-testid="logo-home"
            onClick={() => scrollTo("home")}
            className="flex items-center gap-3 group"
            aria-label="Pizza On The Road"
          >
            <img
              src={LOGO_URL}
              alt="Pizza On The Road"
              className="h-16 md:h-20 lg:h-24 w-auto object-contain transition-transform duration-500 group-hover:rotate-[-4deg]"
            />
            <span className="sr-only">Pizza On The Road</span>
          </button>

          <nav className="hidden md:flex items-center gap-7 lg:gap-9">
            {NAV_ITEMS.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => scrollTo(n.id)}
                className="font-outfit text-[11px] uppercase tracking-[0.22em] text-[#f4f4f0]/80 hover:text-[#f4f4f0] relative group"
              >
                {t.nav[n.key]}
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#800020] scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <LangToggle lang={lang} setLang={setLang} />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((s) => !s)}
              className="md:hidden p-2 -mr-2 text-[#f4f4f0]"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <MenuIcon size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden border-t border-[#800020]/25 bg-[#121212]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_ITEMS.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-${n.id}`}
                  onClick={() => scrollTo(n.id)}
                  className="font-outfit text-left text-sm uppercase tracking-[0.22em] text-[#f4f4f0]/85 hover:text-[#800020] transition-colors"
                >
                  {t.nav[n.key]}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10">
        <Hero t={t} scrollTo={scrollTo} />
        <Menu t={t} openAcc={openAcc} setOpenAcc={setOpenAcc} lang={lang} />
        <Gallery t={t} />
        <Address t={t} lang={lang} />
        <HoursContact t={t} />
        <Allergens t={t} lang={lang} />
      </main>

      <Footer t={t} lang={lang} />
      <WhatsAppButton t={t} />
      <ConsentBanner lang={lang} />
      {/* Keep <html lang> in sync with current UI language */}
    </div>
  );
};

/* ---------------- LANG TOGGLE ---------------- */
const LangToggle = ({ lang, setLang }) => (
  <div
    data-testid="lang-toggle"
    className="inline-flex items-center font-outfit text-[11px] tracking-[0.22em] uppercase border border-[#800020]/40 rounded-none overflow-hidden"
  >
    {["it", "en"].map((l) => (
      <button
        key={l}
        data-testid={`lang-${l}`}
        onClick={() => setLang(l)}
        className={`px-3 py-1.5 transition-colors ${
          lang === l
            ? "bg-[#800020] text-[#f4f4f0]"
            : "text-[#f4f4f0]/70 hover:text-[#f4f4f0]"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

/* ---------------- OPEN STATUS PILL ---------------- */
const OpenStatusPill = ({ t }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  // 0 Sun, 1 Mon, 2 Tue ... 6 Sat. Monday always closed. Open 16:30-20:30 other days.
  const day = now.getDay();
  const hour = now.getHours() + now.getMinutes() / 60;
  const isMondayClosed = day === 1;
  const isOpen = !isMondayClosed && hour >= 16.5 && hour < 20.5;
  const label = isMondayClosed
    ? t.status.closedMonday
    : isOpen
    ? `${t.status.openNow} · 16:30 – 20:30`
    : `${t.status.closedNow} · ${t.status.opens} 16:30`;
  const dotColor = isOpen ? "#5BBF7A" : isMondayClosed ? "#9e0028" : "#D4A574";
  return (
    <span
      data-testid="hero-open-status"
      className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[#800020]/40 bg-[#1a1a1a]/70 backdrop-blur-sm font-outfit text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-[#f4f4f0]/85"
    >
      <span className="relative flex h-2 w-2">
        {isOpen && (
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
            style={{ background: dotColor }}
          />
        )}
        <span
          className="relative inline-flex rounded-full h-2 w-2"
          style={{ background: dotColor }}
        />
      </span>
      {label}
    </span>
  );
};

/* ---------------- TOO GOOD TO GO BADGE ---------------- */
const TgtgBadge = ({ t }) => (
  <a
    data-testid="tgtg-badge"
    href={TGTG_URL}
    target="_blank"
    rel="noopener noreferrer"
    title={t.partner.tgtgText}
    aria-label={t.partner.tgtgText}
    className="group inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full shadow-lg shadow-[#0d4f48]/40 ring-1 ring-white/10 transition-all duration-300 hover:shadow-xl hover:shadow-[#0d4f48]/50 hover:-translate-y-[1px] active:scale-[0.98]"
    style={{
      background:
        "linear-gradient(180deg, #5cc5b5 0%, #2da495 55%, #1a8a7c 100%)",
    }}
  >
    <span className="grid place-items-center w-7 h-7 md:w-8 md:h-8 bg-white/95 rounded-full shadow-inner">
      <img
        src={TGTG_LOGO_URL}
        alt="Too Good To Go"
        className="h-4 md:h-5 w-auto object-contain"
        loading="lazy"
      />
    </span>
    <span className="font-outfit text-[11px] md:text-[12px] font-semibold tracking-[0.04em] text-white">
      Too Good To Go
    </span>
  </a>
);

/* ---------------- HERO ---------------- */
const Hero = ({ t, scrollTo }) => (
  <section
    id="home"
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16"
  >
    <div className="max-w-6xl mx-auto text-center">
      <div
        className="reveal mb-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-5"
        style={{ animationDelay: "0.1s" }}
      >
        <OpenStatusPill t={t} />
        <TgtgBadge t={t} />
      </div>
      <h1
        data-testid="hero-title"
        className="chalk-in font-caveat text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] text-[#f4f4f0]"
      >
        {t.hero.title}
        <span className="block text-[#9e0028]">{t.hero.titleAlt}</span>
      </h1>
      <p
        data-testid="hero-sub"
        className="reveal mt-8 max-w-xl mx-auto font-outfit text-sm md:text-base text-[#a1a1aa] leading-relaxed"
        style={{ animationDelay: "0.9s" }}
      >
        {t.hero.sub}
      </p>

      <div
        className="reveal mt-12 flex flex-col sm:flex-row gap-3 justify-center items-center"
        style={{ animationDelay: "1.1s" }}
      >
        <button
          data-testid="hero-cta"
          onClick={() => scrollTo("menu")}
          className="group inline-flex items-center gap-3 bg-[#800020] hover:bg-[#9e0028] text-[#f4f4f0] px-7 py-4 font-outfit text-[11px] uppercase tracking-[0.28em] border border-[#f4f4f0]/10 transition-all duration-300 hover:translate-y-[-1px]"
        >
          {t.hero.cta}
          <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
        </button>
        <a
          data-testid="hero-call"
          href={`tel:${t.contact.phone.replace(/\s/g, "")}`}
          className="inline-flex items-center gap-2 px-7 py-4 font-outfit text-[11px] uppercase tracking-[0.28em] text-[#f4f4f0]/80 hover:text-[#f4f4f0] border border-[#f4f4f0]/15 hover:border-[#f4f4f0]/35 transition-colors"
        >
          <Phone size={14} /> {t.hero.ghost}
        </a>
        <button
          data-testid="hero-where"
          onClick={() => scrollTo("address")}
          className="inline-flex items-center gap-2 px-7 py-4 font-outfit text-[11px] uppercase tracking-[0.28em] text-[#f4f4f0]/80 hover:text-[#f4f4f0] border border-[#f4f4f0]/15 hover:border-[#f4f4f0]/35 transition-colors"
        >
          <MapPin size={14} /> {t.hero.where}
        </button>
      </div>

      {/* Compact hours row visible immediately */}
      <div
        className="reveal mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-outfit text-[11px] uppercase tracking-[0.28em] text-[#f4f4f0]/65"
        style={{ animationDelay: "1.25s" }}
        data-testid="hero-hours-strip"
      >
        <span className="flex items-center gap-2">
          <Clock size={13} className="text-[#9e0028]" />
          {t.hours.weekdays} · {t.hours.weekdaysTime}
        </span>
        <span className="hidden sm:inline w-px h-3 bg-[#f4f4f0]/20" />
        <span className="text-[#f4f4f0]/55">
          {t.hours.weekend} · {t.hours.weekendTime}
        </span>
      </div>
    </div>

    {/* Scroll cue */}
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-[#f4f4f0]/30 pointer-events-none">
      <span className="font-outfit text-[9px] tracking-[0.3em] uppercase">{t.misc.scroll}</span>
      <span className="w-px h-8 bg-gradient-to-b from-[#f4f4f0]/30 to-transparent" />
    </div>
  </section>
);

/* ---------------- SECTION HEADING ---------------- */
const SectionHeading = ({ label, title, id }) => (
  <div className="mb-6 md:mb-8">
    <div className="flex items-center gap-3 mb-2">
      <span className="w-8 h-px bg-[#800020]" />
      <span
        data-testid={`${id}-label`}
        className="font-outfit text-[10px] tracking-[0.4em] uppercase text-[#800020]"
      >
        {label}
      </span>
    </div>
    <h2
      data-testid={`${id}-title`}
      className="font-caveat text-5xl md:text-6xl text-[#f4f4f0]"
    >
      {title}
    </h2>
  </div>
);

/* ---------------- MENU ---------------- */
const Menu = ({ t, openAcc, setOpenAcc, lang }) => (
  <section id="menu" data-testid="menu-section" className="px-6 md:px-10 py-10 md:py-14">
    <div className="max-w-6xl mx-auto">
      <SectionHeading id="menu" label={t.sections.menuSub} title={t.sections.menu} />

      <div
        data-testid="menu-allergen-notice"
        className="mb-6 md:mb-8 flex items-start gap-3 px-4 py-3 border border-[#800020]/35 bg-[#800020]/10 rounded-md"
        role="note"
      >
        <span
          aria-hidden="true"
          className="mt-[2px] inline-flex items-center justify-center w-5 h-5 rounded-full border border-[#9e0028] text-[#e6c2c9] text-[10px] font-bold shrink-0"
        >
          !
        </span>
        <p className="font-outfit text-[11.5px] md:text-[12.5px] leading-relaxed text-[#e6c2c9]">
          {t.sections.allergensInline}
        </p>
      </div>

      <div className="divide-y divide-[#800020]/25 border-t border-b border-[#800020]/25">
        {menuData.map((cat) => {
          const isOpen = openAcc === cat.id;
          return (
            <div key={cat.id} data-testid={`accordion-${cat.id}`}>
              <button
                data-testid={`accordion-trigger-${cat.id}`}
                onClick={() => setOpenAcc(isOpen ? null : cat.id)}
                className="w-full flex items-center justify-between py-7 md:py-9 text-left group"
                aria-expanded={isOpen}
              >
                <span
                  className={`font-caveat text-3xl md:text-5xl transition-colors ${
                    isOpen ? "text-[#9e0028]" : "text-[#f4f4f0] group-hover:text-[#9e0028]"
                  }`}
                >
                  {t.categories[cat.id]}
                </span>
                <span
                  className={`w-9 h-9 md:w-11 md:h-11 grid place-items-center border border-[#800020]/50 transition-all duration-500 ${
                    isOpen ? "rotate-45 bg-[#800020] border-[#800020]" : "rotate-0"
                  }`}
                >
                  <Plus size={16} className="text-[#f4f4f0]" />
                </span>
              </button>

              <div
                className="grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
              >
                <div className="overflow-hidden">
                  <div className="pb-10 md:pb-14 grid md:grid-cols-2 gap-x-12 gap-y-7">
                    {cat.items.map((item, i) => {
                      const meta = t.items[item.key];
                      return (
                        <div
                          key={item.key}
                          data-testid={`menu-item-${item.key}`}
                          className="reveal"
                          style={{
                            animationDelay: `${0.05 + i * 0.04}s`,
                            animationDuration: "0.55s",
                          }}
                        >
                          <div className="flex items-baseline">
                            <span className="font-outfit text-base md:text-lg text-[#f4f4f0]">
                              {meta.n}
                            </span>
                            <span className="dot-leader" />
                            <span className="font-outfit text-base md:text-lg text-[#f4f4f0]/90">
                              € {item.price}
                            </span>
                          </div>
                          {meta.d && (
                            <p className="font-outfit text-xs md:text-sm text-[#a1a1aa] mt-1.5 leading-relaxed">
                              {meta.d}
                            </p>
                          )}
                          {item.allergens && item.allergens.length > 0 && (
                            <AllergenBadges
                              nums={item.allergens}
                              testid={`allergens-${item.key}`}
                              lang={lang}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- HOURS + CONTACT ---------------- */
const HoursContact = ({ t }) => {
  return (
  <section
    id="hours"
    data-testid="hours-contact-section"
    className="px-6 md:px-10 py-10 md:py-14"
  >
    <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20">
      {/* Hours */}
      <div data-testid="hours-block">
        <SectionHeading id="hours" label="03" title={t.sections.hours} />
        <div className="space-y-10">
          <Row
            icon={<Clock size={16} />}
            label={t.hours.weekdays}
            value={t.hours.weekdaysTime}
            testid="hours-weekdays"
          />
          <Row
            icon={<Clock size={16} />}
            label={t.hours.weekend}
            value={t.hours.weekendTime}
            testid="hours-weekend"
            accent
          />
        </div>
      </div>

      {/* Contact */}
      <div id="contact" data-testid="contact-block">
        <SectionHeading id="contact" label="04" title={t.sections.contact} />
        <div className="space-y-6">
          <a
            data-testid="contact-phone"
            href={`tel:${t.contact.phone.replace(/\s/g, "")}`}
            className="block group"
          >
            <Row
              icon={<Phone size={16} />}
              label={t.contact.phoneLabel}
              value={t.contact.phone}
              hover
            />
          </a>
          <a
            data-testid="contact-email"
            href={`mailto:${t.contact.email}`}
            className="block group"
          >
            <Row
              icon={<Mail size={16} />}
              label={t.contact.emailLabel}
              value={t.contact.email}
              hover
            />
          </a>
          <a
            data-testid="contact-cta"
            href={`tel:${t.contact.phone.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-3 mt-4 bg-[#800020] hover:bg-[#9e0028] text-[#f4f4f0] px-6 py-3.5 font-outfit text-[11px] uppercase tracking-[0.28em] border border-[#f4f4f0]/10 transition-all duration-300 hover:translate-y-[-1px]"
          >
            {t.contact.callCta}
            <span>→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
  );
};

const Row = ({ icon, label, value, hover = false, accent = false, testid }) => (
  <div data-testid={testid} className="flex items-start gap-4">
    <span
      className={`mt-1.5 grid place-items-center w-8 h-8 border ${
        accent ? "border-[#9e0028] text-[#9e0028]" : "border-[#800020]/50 text-[#f4f4f0]/70"
      }`}
    >
      {icon}
    </span>
    <div>
      <p className="font-outfit text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa] mb-1">
        {label}
      </p>
      <p
        className={`font-outfit text-base md:text-lg text-[#f4f4f0] ${
          hover ? "group-hover:text-[#9e0028] transition-colors" : ""
        }`}
      >
        {value}
      </p>
    </div>
  </div>
);

/* ---------------- ADDRESS ---------------- */
const Address = ({ t, lang }) => {
  const mapsQuery = encodeURIComponent(
    `${t.address.name}, ${t.address.street}, ${t.address.city}`
  );
  const embedSrc = `https://maps.google.com/maps?q=${mapsQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  const openMapsLink = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;

  // Only load the iframe if the user has consented to third-party cookies
  const [thirdpartyOk, setThirdpartyOk] = useState(() => {
    try {
      const prefs = window.ptrConsent && window.ptrConsent.getPrefs();
      return !!(prefs && prefs.thirdparty);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = (e) => setThirdpartyOk(!!(e.detail && e.detail.thirdparty));
    window.addEventListener("ptr-consent-change", handler);
    return () => window.removeEventListener("ptr-consent-change", handler);
  }, []);

  const placeholderText =
    lang === "it"
      ? {
          title: "Mappa interattiva",
          desc: "Per visualizzare la mappa Google Maps devi acconsentire ai cookie di terze parti.",
          enable: "Attiva la mappa",
          open: "Apri in Google Maps",
        }
      : {
          title: "Interactive map",
          desc: "To display the Google Maps embed you need to consent to third-party cookies.",
          enable: "Enable map",
          open: "Open in Google Maps",
        };

  return (
    <section
      id="address"
      data-testid="address-section"
      className="px-6 md:px-10 py-10 md:py-14 border-t border-[#800020]/20"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeading id="address" label="02" title={t.sections.address} />

        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-stretch">
          {/* Address details */}
          <div className="flex">
            <div className="flex items-start gap-4">
              <span className="mt-1.5 grid place-items-center w-9 h-9 border border-[#800020] text-[#9e0028] shrink-0">
                <MapPin size={16} />
              </span>
              <div className="font-outfit space-y-1.5">
                <p data-testid="address-name" className="text-lg md:text-xl text-[#f4f4f0]">
                  {t.address.name}
                </p>
                <p data-testid="address-street" className="text-[#f4f4f0]/85">
                  {t.address.street}
                </p>
                <p data-testid="address-city" className="text-[#f4f4f0]/85">
                  {t.address.city}
                </p>
                <p data-testid="address-region" className="text-[#a1a1aa] text-sm">
                  {t.address.region}
                </p>
                <a
                  data-testid="address-directions"
                  href={openMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-5 font-outfit text-[11px] uppercase tracking-[0.28em] text-[#f4f4f0] hover:text-[#9e0028] transition-colors border-b border-[#800020]/40 hover:border-[#9e0028] pb-1"
                >
                  {t.address.directions} →
                </a>
              </div>
            </div>
          </div>

          {/* Map — iframe only after consent, otherwise clickable placeholder */}
          <div
            data-testid="address-map"
            className="relative h-[280px] md:h-auto md:min-h-[280px] w-full border border-[#800020]/60 overflow-hidden bg-[#1a1a1a]"
          >
            {thirdpartyOk ? (
              <>
                <iframe
                  title="Pizza On The Road · Mappa"
                  src={embedSrc}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.9)" }}
                />
                <div className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-[#800020]/30" />
              </>
            ) : (
              <div
                data-testid="map-placeholder"
                className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-[#141414]"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 50% 50%, rgba(128,0,32,0.15), transparent 60%)",
                }}
              >
                <MapPin size={30} className="text-[#9e0028] mb-3" />
                <p className="font-caveat text-3xl text-[#f4f4f0] mb-2">
                  {placeholderText.title}
                </p>
                <p className="font-outfit text-[12px] text-[#a1a1aa] max-w-xs mb-5">
                  {placeholderText.desc}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    data-testid="map-enable"
                    onClick={() =>
                      window.dispatchEvent(new Event("ptr-open-preferences"))
                    }
                    className="px-5 py-2.5 bg-[#800020] hover:bg-[#9e0028] text-white font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full transition-colors"
                  >
                    {placeholderText.enable}
                  </button>
                  <a
                    data-testid="map-open-external"
                    href={openMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 border border-white/20 text-white font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full hover:border-white/40 transition-colors"
                  >
                    {placeholderText.open}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- GALLERY (mosaic + lightbox) ---------------- */
const Gallery = ({ t }) => {
  const [lightbox, setLightbox] = useState(null);

  // Lock body scroll + hide header (lang toggle / mobile menu) when lightbox open
  useEffect(() => {
    if (lightbox !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.body.classList.add("lightbox-open");
      return () => {
        document.body.style.overflow = prev;
        document.body.classList.remove("lightbox-open");
      };
    }
  }, [lightbox]);

  // Mosaic spans: smaller mobile (2 col) + desktop (4 col)
  const tiles = [
    "col-span-2 row-span-2 md:col-span-2 md:row-span-2", // 0 — hero
    "col-span-1 row-span-1 md:col-span-2 md:row-span-1", // 1 — top right wide
    "col-span-1 row-span-1 md:col-span-1 md:row-span-1", // 2 — small
    "col-span-2 row-span-1 md:col-span-1 md:row-span-2", // 3
    "col-span-2 row-span-1 md:col-span-3 md:row-span-1", // 4 — bottom wide
  ];

  return (
    <>
      <section id="gallery" data-testid="gallery-section" className="px-6 md:px-10 py-10 md:py-14">
        <div className="max-w-[1440px] mx-auto">
          <div className="max-w-6xl mx-auto">
            <SectionHeading id="gallery" label="01" title={t.sections.gallery} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[110px] sm:auto-rows-[130px] md:auto-rows-[200px] lg:auto-rows-[220px] gap-2 md:gap-4">
            {galleryImages.map((img, i) => (
              <button
                key={i}
                type="button"
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(i)}
                aria-label={img.alt}
                className={`relative overflow-hidden border border-[#800020]/70 bg-[#1a1a1a] group cursor-zoom-in ${tiles[i] || ""}`}
              >
                <img
                  src={img.url}
                  alt={img.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover grayscale-[10%] group-hover:grayscale-0 group-hover:scale-[1.06] transition-all duration-[900ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/55 via-transparent to-transparent opacity-70 group-hover:opacity-30 transition-opacity duration-500" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0a0a]/95 backdrop-blur-sm p-4 md:p-10 animate-[reveal_0.3s_ease-out]"
          role="dialog"
          aria-modal="true"
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(null);
            }}
            aria-label="Chiudi"
            className="absolute top-5 right-5 grid place-items-center w-11 h-11 rounded-full border border-white/20 bg-black/40 hover:bg-[#800020] hover:border-[#800020] text-white transition-colors"
          >
            <X size={20} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((lightbox - 1 + galleryImages.length) % galleryImages.length);
            }}
            aria-label="Precedente"
            className="hidden md:grid absolute left-6 top-1/2 -translate-y-1/2 place-items-center w-12 h-12 rounded-full border border-white/20 bg-black/40 hover:bg-[#800020] hover:border-[#800020] text-white transition-colors"
          >
            ←
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((lightbox + 1) % galleryImages.length);
            }}
            aria-label="Successivo"
            className="hidden md:grid absolute right-6 top-1/2 -translate-y-1/2 place-items-center w-12 h-12 rounded-full border border-white/20 bg-black/40 hover:bg-[#800020] hover:border-[#800020] text-white transition-colors"
          >
            →
          </button>
          <img
            src={galleryImages[lightbox].url}
            alt={galleryImages[lightbox].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[88vh] max-w-[92vw] object-contain border border-[#800020]/50 shadow-2xl"
          />
        </div>
      )}
    </>
  );
};

/* ---------------- ALLERGEN BADGES ---------------- */
const AllergenBadges = ({ nums, testid, lang }) => (
  <div
    data-testid={testid}
    className="mt-2 flex flex-wrap gap-1.5 items-center"
  >
    {nums.map((n) => {
      const meta = allergenList.find((a) => a.id === n);
      const title = meta ? (lang === "it" ? meta.it : meta.en) : "";
      return <AllergenIcon key={n} id={n} size={22} title={title} />;
    })}
  </div>
);

/* ---------------- ALLERGENS SECTION ---------------- */
const Allergens = ({ t, lang }) => {
  const [open, setOpen] = useState(false);
  return (
    <section
      id="allergens"
      data-testid="allergens-section"
      className="px-6 md:px-10 py-10 md:py-14 border-t border-[#800020]/20"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeading id="allergens" label="05" title={t.sections.allergens} />
        <p
          data-testid="allergens-sub"
          className="font-outfit text-xs md:text-sm text-[#a1a1aa] -mt-4 mb-6 leading-relaxed max-w-2xl"
        >
          {t.sections.allergensSub}
        </p>

        <div className="border-t border-b border-[#800020]/25">
          <button
            data-testid="allergens-toggle"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center justify-between py-7 md:py-9 text-left group"
            aria-expanded={open}
          >
            <span
              className={`font-caveat text-3xl md:text-5xl transition-colors ${
                open ? "text-[#9e0028]" : "text-[#f4f4f0] group-hover:text-[#9e0028]"
              }`}
            >
              {t.sections.allergensToggle}
            </span>
            <span
              className={`w-9 h-9 md:w-11 md:h-11 grid place-items-center border border-[#800020]/50 transition-all duration-500 ${
                open ? "rotate-45 bg-[#800020] border-[#800020]" : "rotate-0"
              }`}
            >
              <Plus size={16} className="text-[#f4f4f0]" />
            </span>
          </button>

          <div
            className="grid transition-[grid-template-rows] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <div className="pb-10 md:pb-14">
                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                  {allergenList.map((a) => (
                    <li
                      key={a.id}
                      data-testid={`allergen-${a.id}`}
                      className="flex items-start gap-3"
                    >
                      <AllergenIcon
                        id={a.id}
                        size={30}
                        title={lang === "it" ? a.it : a.en}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-outfit text-sm text-[#f4f4f0] leading-tight">
                          {lang === "it" ? a.it : a.en}
                        </p>
                        <p className="font-outfit text-[11px] text-[#a1a1aa] mt-0.5 leading-snug">
                          {lang === "it" ? a.full_it : a.full_en}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                <p
                  data-testid="allergens-note"
                  className="font-outfit text-[11px] md:text-xs text-[#a1a1aa]/80 italic mt-8 max-w-2xl leading-relaxed"
                >
                  {t.sections.allergensNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- WHATSAPP FLOATING BUTTON ---------------- */
const WhatsAppLogo = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path
      fill="#fff"
      d="M16.04 4c-6.6 0-11.96 5.36-11.96 11.96 0 2.11.55 4.17 1.6 5.99L4 28l6.2-1.63a11.9 11.9 0 005.83 1.49h.01c6.6 0 11.96-5.36 11.96-11.96 0-3.2-1.24-6.2-3.5-8.46A11.88 11.88 0 0016.04 4zm0 21.9h-.01a9.9 9.9 0 01-5.05-1.38l-.36-.22-3.75.98 1-3.66-.24-.38a9.9 9.9 0 01-1.51-5.27c0-5.48 4.46-9.94 9.95-9.94 2.66 0 5.15 1.04 7.03 2.92a9.87 9.87 0 012.91 7.03c0 5.48-4.46 9.94-9.94 9.94zm5.46-7.45c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35z"
    />
  </svg>
);

const WhatsAppButton = ({ t }) => {
  const [open, setOpen] = useState(false);
  const w = t.whatsapp;
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(w.message)}`;

  return (
    <div className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[70] flex flex-col items-end gap-3">
      {/* Info / tips card */}
      {open && (
        <div
          data-testid="whatsapp-card"
          className="w-[280px] md:w-[320px] rounded-2xl bg-[#111b21] border border-[#25D366]/30 shadow-2xl shadow-black/50 overflow-hidden animate-[reveal_0.25s_ease-out]"
        >
          <div className="flex items-center gap-2.5 px-4 py-3 bg-[#25D366]">
            <WhatsAppLogo size={22} />
            <span className="font-outfit text-[13px] font-semibold text-white tracking-wide">
              {w.tipTitle}
            </span>
            <button
              data-testid="whatsapp-close"
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="ml-auto text-white/90 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
          <div className="px-4 py-4">
            <ul className="space-y-2.5 mb-4">
              {w.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[#25D366] shrink-0" />
                  <span className="font-outfit text-[12.5px] leading-snug text-[#e9edef]">
                    {tip}
                  </span>
                </li>
              ))}
            </ul>
            <a
              data-testid="whatsapp-open-chat"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#25D366] hover:bg-[#1fbc59] text-white font-outfit text-[13px] font-semibold tracking-wide transition-colors"
            >
              <WhatsAppLogo size={18} /> {w.label}
            </a>
          </div>
        </div>
      )}

      {/* Floating action button */}
      <button
        data-testid="whatsapp-fab"
        onClick={() => setOpen((s) => !s)}
        aria-label={w.label}
        className="group flex items-center gap-2.5 h-14 md:h-15 pl-3.5 pr-3.5 md:pr-5 rounded-full bg-[#25D366] hover:bg-[#1fbc59] shadow-xl shadow-[#25D366]/30 hover:shadow-[#25D366]/50 transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
      >
        <span className="relative flex items-center justify-center">
          <span className="absolute inline-flex h-full w-full rounded-full bg-white/30 opacity-0 group-hover:opacity-100 animate-ping" />
          <WhatsAppLogo size={30} />
        </span>
        <span className="hidden md:inline font-outfit text-[13px] font-semibold text-white tracking-wide pr-1">
          {w.short}
        </span>
      </button>
    </div>
  );
};

/* ---------------- FOOTER ---------------- */
const Footer = ({ t, lang }) => {
  const privHref = lang === "en" ? "/en/privacy-policy/" : "/privacy-policy/";
  const cookHref = lang === "en" ? "/en/cookie-policy/" : "/cookie-policy/";
  const legalLabels =
    lang === "en"
      ? {
          privacy: "Privacy Policy",
          cookies: "Cookie Policy",
          manage: "Manage cookie preferences",
        }
      : {
          privacy: "Privacy Policy",
          cookies: "Cookie Policy",
          manage: "Gestisci preferenze cookie",
        };
  return (
  <footer
    data-testid="site-footer"
    className="relative z-10 border-t border-[#800020]/30 px-6 md:px-10 pt-12 pb-6"
  >
    <div className="max-w-[1440px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <img src={LOGO_URL} alt="" className="h-8 w-auto opacity-90" />
        <span className="font-caveat text-xl text-[#f4f4f0]/85">Pizza On The Road</span>
      </div>
      <p className="font-outfit text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa] text-center">
        {t.footer.tagline}
      </p>
      <p className="font-outfit text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
        © {new Date().getFullYear()} · {t.footer.rights}
      </p>
    </div>

    <nav
      data-testid="footer-legal"
      aria-label={lang === "en" ? "Legal" : "Legale"}
      className="max-w-[1440px] mx-auto mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-outfit text-[11px] uppercase tracking-[0.22em] text-[#a1a1aa]"
    >
      <a href={privHref} className="hover:text-[#f4f4f0] transition-colors">
        {legalLabels.privacy}
      </a>
      <span aria-hidden="true" className="opacity-30">·</span>
      <a href={cookHref} className="hover:text-[#f4f4f0] transition-colors">
        {legalLabels.cookies}
      </a>
      <span aria-hidden="true" className="opacity-30">·</span>
      <button
        type="button"
        data-testid="footer-manage-cookies"
        onClick={() => window.dispatchEvent(new Event("ptr-open-preferences"))}
        className="hover:text-[#f4f4f0] transition-colors uppercase tracking-[0.22em] font-outfit text-[11px]"
      >
        {legalLabels.manage}
      </button>
    </nav>

    <p
      data-testid="legal-info"
      className="max-w-[1440px] mx-auto mt-8 pt-5 border-t border-white/[0.04] font-outfit text-[9px] md:text-[10px] leading-relaxed text-[#f4f4f0]/25 text-center"
    >
      Lema di Valentini Ilaria &amp; C. S.a.s. · Sede legale: Via Gaetano Properzi 219B, 63822 Porto San Giorgio (FM) · P.IVA 02517800443 · REA FM-290781
    </p>
  </footer>
  );
};

export default PizzaSite;
